import React, { useState } from 'react';
import { ShieldCheck, Globe, ArrowRight, Loader2, Landmark, CheckCircle2, Check, ChevronsUpDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from '@/lib/axios';

interface Bank {
  code: string;
  name: string;
}

interface NetBankingFetchProps {
  banks: Bank[];
  onInitiated: (requestId: string) => void;
}

const NetBankingFetch: React.FC<NetBankingFetchProps> = ({ banks, onInitiated }) => {
  const [selectedBank, setSelectedBank] = useState<string>('');
  const [isInitiating, setIsInitiating] = useState(false);
  const [open, setOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      // Spec format: { source : "cartnetbanking.com", "status" : "SUCCESS/ERROR", "request":"REQ..." }
      if (data && (data.source === "cartnetbanking.com" || data.status)) {
        console.log("CART Message Received:", data);
        if (data.status === "SUCCESS") {
          toast.success("Bank connection successful! Starting analysis...");
          if (currentRequestId || data.request) {
            onInitiated(currentRequestId || data.request);
          }
          setIframeUrl(null);
        } else if (data.status === "ERROR") {
          toast.error("Bank connection failed. Please try again or upload PDF.");
          setIframeUrl(null);
        }
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onInitiated, currentRequestId]);

  const handleInitiate = async () => {
    if (!selectedBank) {
      toast.error('Please select a bank');
      return;
    }

    setIsInitiating(true);

    try {
      const response = await axios.post('/api/los/orchestrator/', {
        components: ['LOS_InitiateNetBanking_022'],
        LOS_InitiateNetBanking_022: {
          bank_code: selectedBank,
          account_type: 'Saving'
        }
      });

      const result = response.data['LOS_InitiateNetBanking_022'];
      
      if (result.status === 'success' || result.data?.status === 'success') {
        const data = result.data || result;
        const netbankingUrl = data.netbanking_url;
        const requestId = data.request_id;
        
        setCurrentRequestId(requestId);
        setIframeUrl(netbankingUrl);
        toast.info('Opening secure bank portal...');
      } else {
        throw new Error(result.message || 'Failed to initiate NetBanking');
      }
    } catch (error: any) {
      console.error('NetBanking initiation error:', error);
      toast.error('Failed to connect to bank. Please try manual upload.');
    } finally {
      setIsInitiating(false);
    }
  };

  if (iframeUrl) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="space-y-6"
      >
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2 text-emerald-600">
            <ShieldCheck size={20} />
            <span className="text-sm font-bold uppercase tracking-wider">Secure Connection</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIframeUrl(null)}
            className="text-gray-400 hover:text-gray-600 font-bold text-xs"
          >
            CANCEL
          </Button>
        </div>
        <div className="w-full min-h-[700px] h-[80vh] md:h-[750px] rounded-3xl overflow-hidden border-2 border-gray-100 shadow-2xl bg-white relative z-50 pointer-events-auto">
          <iframe 
            src={iframeUrl} 
            className="w-full h-full pointer-events-auto"
            frameBorder="0"
            title="NetBanking Portal"
            allow="payment"
          />
        </div>


        <p className="text-[10px] text-center text-gray-400 italic">
          Powerd by CART Secure Gateway. Your credentials are never shared with us.
        </p>
      </motion.div>
    );
  }


  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid gap-8">
        <div className="flex flex-col items-center text-center space-y-3 pb-2">
          <div className="bg-amber-100 p-4 rounded-3xl text-amber-600 mb-2">
            <Globe size={32} />
          </div>
          <h3 className="text-xl font-bold text-gray-800">Secure Auto-Fetch</h3>
          <p className="text-sm text-gray-500 max-w-[280px]">
            The fastest way to share statements. No downloads, no passwords needed.
          </p>
        </div>

        {/* Bank Selection */}
        <div className="space-y-3">
          <Label className="text-gray-600 font-medium ml-2 flex items-center gap-2">
            <Landmark size={16} className="text-gray-400" />
            Select Your Primary Bank
          </Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full h-16 rounded-2xl border-gray-200 bg-gray-50/50 hover:bg-gray-100/50 justify-between text-left font-normal border-2 focus:border-intellend-green focus:ring-0"
              >
                {selectedBank
                  ? banks.find((bank) => bank.code === selectedBank)?.name
                  : "Search for your bank..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 max-h-[300px]" align="start">
              <Command>
                <CommandInput placeholder="Search bank..." />
                <CommandEmpty>No bank found.</CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    {banks.map((bank) => (
                      <CommandItem
                        key={bank.code}
                        value={bank.name}
                        onSelect={() => {
                          setSelectedBank(bank.code);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedBank === bank.code ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {bank.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Benefit Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100/50">
            <ShieldCheck size={18} className="text-emerald-600 mb-2" />
            <p className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider mb-1">Encrypted</p>
            <p className="text-[10px] text-emerald-700 leading-tight">Bank-grade 256-bit security</p>
          </div>
          <div className="p-4 rounded-2xl bg-blue-50/50 border border-blue-100/50">
            <ArrowRight size={18} className="text-blue-600 mb-2" />
            <p className="text-[11px] font-bold text-blue-800 uppercase tracking-wider mb-1">Instant</p>
            <p className="text-[10px] text-blue-700 leading-tight">Takes less than 60 seconds</p>
          </div>
        </div>

        {/* Action Button */}
        <Button
          onClick={handleInitiate}
          disabled={!selectedBank || isInitiating}
          className={`h-16 rounded-2xl text-lg font-bold transition-all duration-500 shadow-xl active:scale-[0.98]
            ${!selectedBank 
              ? 'bg-gray-100 text-gray-400' 
              : 'bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white shadow-emerald-200 hover:shadow-emerald-300'}`}
        >
          {isInitiating ? (
            <div className="flex items-center gap-3">
              <Loader2 className="animate-spin" size={24} />
              <span>Connecting...</span>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span>Connect Bank Securely</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </div>
          )}
        </Button>

        {/* Footer info */}
        <p className="text-[10px] text-center text-gray-400 font-medium px-6 leading-relaxed">
          By clicking connect, you will be redirected to our secure partner portal to share your digital bank statement.
        </p>
      </div>
    </div>
  );
};

export default NetBankingFetch;
